import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { blogService } from '../services/blogService';
import Seo from '../components/Seo';
import { stripHtml, estimateReadingTime, formatBlogDate } from '../utils/blogHelpers';

const PAGE_SIZE = 6;

const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await blogService.getPublishedPosts();
      setPosts(data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement du blog :', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const names = posts.map((p) => p.categoryName).filter(Boolean);
    return [...new Set(names)];
  }, [posts]);

  const tags = useMemo(() => {
    const all = posts.flatMap((p) => p.tags || []);
    return [...new Set(all)];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return posts.filter((post) => {
      const matchesSearch = !term
        || post.title?.toLowerCase().includes(term)
        || post.excerpt?.toLowerCase().includes(term)
        || stripHtml(post.content).toLowerCase().includes(term);
      const matchesCategory = !selectedCategory || post.categoryName === selectedCategory;
      const matchesTag = !selectedTag || (post.tags || []).includes(selectedTag);
      return matchesSearch && matchesCategory && matchesTag;
    });
  }, [posts, searchTerm, selectedCategory, selectedTag]);

  const visiblePosts = filteredPosts.slice(0, visibleCount);

  return (
    <div className="blog-page">
      <Seo
        title="Blog | El Hiba Para"
        description="Conseils dermocosmétiques, routines de soin et actualités santé par la Parapharmacie El Hiba Ain Aouda."
        canonicalUrl={`${window.location.origin}/blog`}
      />
      <div className="container">
        <div className="page-header">
          <h1><i className="fas fa-newspaper"></i> Blog</h1>
          <p>Conseils, routines et actualités beauté & santé</p>
        </div>

        <div className="blog-filters">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(PAGE_SIZE); }}
            />
          </div>
          {categories.length > 0 && (
            <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setVisibleCount(PAGE_SIZE); }}>
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          )}
          {tags.length > 0 && (
            <select value={selectedTag} onChange={(e) => { setSelectedTag(e.target.value); setVisibleCount(PAGE_SIZE); }}>
              <option value="">Tous les tags</option>
              {tags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
            </select>
          )}
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"><div className="spinner"></div></div>
            <p>Chargement des articles...</p>
          </div>
        ) : error ? (
          <div className="error">
            <h3>❌ Erreur lors du chargement des articles</h3>
            <p>{error}</p>
            <button onClick={loadPosts} className="btn btn-primary">
              <i className="fas fa-refresh"></i> Réessayer
            </button>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="no-products">
            <i className="fas fa-newspaper"></i>
            <h3>Aucun article pour le moment</h3>
            <p>Revenez bientôt pour découvrir nos conseils et actualités.</p>
          </div>
        ) : (
          <>
            <div className="blog-grid">
              {visiblePosts.map((post) => (
                <article key={post.id} className="blog-card">
                  <Link to={`/blog/${post.slug}`} className="blog-card-image-link">
                    <img
                      src={post.featuredImageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=500&h=300&fit=crop'}
                      alt={post.featuredImageAlt || post.title}
                      className="blog-card-image"
                    />
                  </Link>
                  <div className="blog-card-body">
                    {post.categoryName && <span className="blog-card-category">{post.categoryName}</span>}
                    <h2 className="blog-card-title">
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </h2>
                    <p className="blog-card-excerpt">{post.excerpt || stripHtml(post.content).slice(0, 140)}</p>
                    <div className="blog-card-meta">
                      <time dateTime={post.publishedAt?.toDate ? post.publishedAt.toDate().toISOString() : post.publishedAt}>
                        {formatBlogDate(post.publishedAt)}
                      </time>
                      <span>·</span>
                      <span>{estimateReadingTime(post.content)} min de lecture</span>
                    </div>
                    <Link to={`/blog/${post.slug}`} className="blog-card-readmore">
                      Lire la suite <i className="fas fa-arrow-right"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {visibleCount < filteredPosts.length && (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <button className="btn btn-primary btn-large" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                  Charger plus d'articles
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogIndex;
